// src/oura/buyMode.ts
//
// How a business's customers buy, read off a list row (YW, 15 Oct 2026: "we
// are also working with online businesses"). The list used to assume every
// business had a shop in a city; an online store's `city` holds the country it
// sells to, and read as if it were a town.

import { COUNTRY_CODES } from "@component/country-codes-data";
import type { AeoBusiness, AeoBuyMode } from "@service/aeo";

/** The list's Sells column: the New scan's four answers, worded as a fact about the business. */
export const BUY_LABEL: Record<AeoBuyMode, string> = {
    visit: "At outlets",
    online: "Online",
    both: "Outlets and online",
    we_go_to_them: "Home visits",
};

const norm = (place: string) => place.toLowerCase().replace(/[^a-z]+/g, " ").trim();

/** The short forms people type (the backend's `is_country` list keeps the same ones), dotted or not. */
const COUNTRY_ALIASES = [
    "uk", "u.k.", "usa", "u.s.a.", "us", "u.s.", "uae", "u.a.e.",
    "united states of america", "great britain", "britain", "korea",
];

/** Every country by name and alias, through the same `norm` as the place it is checked against, so "U.S.A." matches. */
const COUNTRY_NAMES = new Set([...COUNTRY_CODES.map((c) => c.name), ...COUNTRY_ALIASES].map(norm));

/** A whole country, not a place a customer can be sent. Exact match only: "Kuala Lumpur, Malaysia" is a city. */
export function isCountry(place: string): boolean {
    return COUNTRY_NAMES.has(norm(place));
}

/**
 * How this business sells, and to which country when it sells online.
 *
 * The list's own `buy` when it sends one. Otherwise the backend's rule for a
 * scan made before the New scan asked (`is_country`, aeo_query_spec.py): a
 * whole country where the city goes is an online store selling to it.
 */
export function buyOf(row: Pick<AeoBusiness, "buy" | "market" | "city">): { buy: AeoBuyMode; market: string | null } {
    if (row.buy) return { buy: row.buy, market: row.market ?? null };
    if (row.city && isCountry(row.city)) return { buy: "online", market: row.city };
    return { buy: "visit", market: null };
}

/**
 * The Where and Sells cells: the place a customer finds them (the outlets'
 * city, or the country an online store sells to) and how they buy. Null place
 * when nothing is on file.
 */
export function whereOf(row: Pick<AeoBusiness, "buy" | "market" | "city">): { place: string | null; buy: AeoBuyMode } {
    const { buy, market } = buyOf(row);
    const place = (buy === "online" ? market ?? row.city : row.city)?.trim() || null;
    return { place, buy };
}
